import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'

import { ApiErrorHolder, GeneralApiError } from '../services/error'

type ErrorCode = string

interface ErrorPayload {
  errorCode: ErrorCode
  message: string
  metadata?: any
}

function tryParseGeneralApiError(e: Error): O.Option<GeneralApiError> {
  return e instanceof ApiErrorHolder && e.err.name === 'GeneralApiError'
    ? O.some(e.err)
    : O.none
}

export function tryParseErrorPayload(
  e: GeneralApiError
): O.Option<ErrorPayload> {
  return pipe(
    O.fromNullable(e.cause.response),
    O.map((response) => response.data),
    O.chain((data) => (data.errorCode == null ? O.none : O.some(data)))
  )
}

export function tryParseApiError(e: Error): O.Option<ErrorPayload> {
  return pipe(tryParseGeneralApiError(e), O.chain(tryParseErrorPayload))
}

export const tryParseErrorCode = (e: Error): O.Option<ErrorCode> =>
  pipe(
    tryParseApiError(e),
    O.map((r) => r.errorCode)
  )

// TODO: the below errors shouldn't be here because they are use case specific and this file is for general error
// handling
class InvalidStateTransition {
  public static readonly errorCode = 'InvalidStateTransition'
}

export function tryParseInvalidStateTransition(
  e: Error
): O.Option<InvalidStateTransition> {
  return pipe(
    tryParseApiError(e),
    O.filter((x) => x.errorCode === InvalidStateTransition.errorCode),
    O.map((x) => new InvalidStateTransition())
  )
}

class AllPaymentsFailed {
  public static readonly errorCode = 'AllPaymentsFailed'
}

export function tryParseAllPaymentsFailed(
  e: Error
): O.Option<AllPaymentsFailed> {
  return pipe(
    tryParseApiError(e),
    O.filter((x) => x.errorCode === AllPaymentsFailed.errorCode),
    O.map((x) => new AllPaymentsFailed())
  )
}

type GrantPeriodCreateErrors =
  | 'PeriodConflicted'
  | 'CheckCaseFailed'
  | 'CaseNotFound'
export function tryParseGrantPeriodCreateError(
  e: Error
): O.Option<GrantPeriodCreateError> {
  return pipe(
    tryParseApiError(e),
    O.filter(
      (x) =>
        x.errorCode === 'PeriodConflicted' ||
        x.errorCode === 'CheckCaseFailed' ||
        x.errorCode === 'CaseNotFound'
    ),
    O.map(
      (x) => new GrantPeriodCreateError(x.errorCode as GrantPeriodCreateErrors)
    )
  )
}

class GrantPeriodCreateError {
  constructor(public readonly errorCode: GrantPeriodCreateErrors) {}
}
